import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const Container = styled.div`
  min-height: calc(100vh - 200px);
  padding: 60px 20px;
  background: linear-gradient(135deg, var(--background-accent) 0%, var(--background-lighter) 100%);
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 32px;
  margin-bottom: 10px;
  text-align: center;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 40px;
  font-size: 16px;
`;

const Card = styled(motion.div)`
  background: #FFFFFF;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background: #FFFFFF;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  background: #FFFFFF;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  background: #FFFFFF;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }

  option {
    background: #1a1a1a;
    color: #fff;
  }
`;

const InfoBox = styled.div`
  background: #f8f9fa;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.h3`
  color: var(--primary-color);
  font-size: 18px;
  margin: 0 0 15px 0;
  font-weight: 700;
`;

const InfoText = styled.p`
  color: var(--text-secondary);
  font-size: 14px;
  margin: 8px 0;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border: none;
  border-radius: 10px;
  color: #FFFFFF;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4);
  }

  &:disabled {
    background: #333;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background: #FFFFFF;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: #FFFFFF;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: #4caf50;
  color: #fff;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 20px;
`;

const ErrorMessage = styled(motion.div)`
  background: #f44336;
  color: #fff;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 20px;
`;

const PlaceOrder = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [cart, setCart] = useState([]);
  const [membership, setMembership] = useState(null);

  const [formData, setFormData] = useState({
    pickupAddress: "",
    deliveryAddress: "",
    pickupDate: "",
    pickupTimeSlot: "",
    deliveryDate: "",
    deliveryTimeSlot: "",
    notes: "",
    phone: "",
  });

  // Load cart from session storage
  useEffect(() => {
    const savedCart = sessionStorage.getItem("orderCart");
    const savedMembership = sessionStorage.getItem("orderMembership");

    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }

    if (savedMembership) {
      setMembership(JSON.parse(savedMembership));
    }
  }, []);

  // Calculate minimum pickup date (next available slot)
  const getMinPickupDate = () => {
    const now = new Date();
    const currentHour = now.getHours();

    // If after 7 PM, next available is tomorrow
    if (currentHour >= 19) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split("T")[0];
    }

    // Otherwise, can book for today
    return now.toISOString().split("T")[0];
  };

  // Calculate minimum delivery date (24 hours after pickup)
  const getMinDeliveryDate = () => {
    if (!formData.pickupDate) return "";

    const pickup = new Date(formData.pickupDate);
    pickup.setDate(pickup.getDate() + 1); // Minimum 24 hours (1 day)
    return pickup.toISOString().split("T")[0];
  };

  // Calculate maximum delivery date (3 days after pickup)
  const getMaxDeliveryDate = () => {
    if (!formData.pickupDate) return "";

    const pickup = new Date(formData.pickupDate);
    pickup.setDate(pickup.getDate() + 3); // Maximum 3 days
    return pickup.toISOString().split("T")[0];
  };

  // Get available time slots based on current time
  const getAvailablePickupSlots = () => {
    const slots = [
      { value: "10-2", label: "10:00 AM - 2:00 PM" },
      { value: "2-5", label: "2:00 PM - 5:00 PM" },
      { value: "5-7", label: "5:00 PM - 7:00 PM" },
    ];

    // If pickup date is today, filter out past slots
    if (formData.pickupDate === new Date().toISOString().split("T")[0]) {
      const currentHour = new Date().getHours();

      return slots.filter((slot) => {
        if (slot.value === "10-2" && currentHour >= 14) return false;
        if (slot.value === "2-5" && currentHour >= 17) return false;
        if (slot.value === "5-7" && currentHour >= 19) return false;
        return true;
      });
    }

    return slots;
  };

  const deliveryTimeSlots = [
    { value: "10-2", label: "10:00 AM - 2:00 PM" },
    { value: "2-5", label: "2:00 PM - 5:00 PM" },
    { value: "5-7", label: "5:00 PM - 7:00 PM" },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate minimum order quantity (10 items)
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    if (totalItems < 10) {
      const itemsNeeded = 10 - totalItems;
      setError(`⚠️ Minimum order quantity is 10 items. You currently have ${totalItems} ${totalItems === 1 ? 'item' : 'items'} in your cart. Please add ${itemsNeeded} more ${itemsNeeded === 1 ? 'item' : 'items'} to proceed with your order.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Prevent multiple submissions
    if (submitting) {
      return;
    }
    
    setSubmitting(true);
    setLoading(true);
    setError("");

    try {
      // Validate required fields
      if (
        !formData.pickupAddress ||
        !formData.pickupDate ||
        !formData.pickupTimeSlot
      ) {
        setError("Please fill in all required fields including time slot");
        setLoading(false);
        return;
      }

      // Validate delivery date if provided
      if (formData.deliveryDate && !formData.deliveryTimeSlot) {
        setError("Please select a delivery time slot");
        setLoading(false);
        return;
      }

      // Detect service type from cart items (use most common service type)
      const serviceTypeCounts = cart.reduce((acc, item) => {
        acc[item.service_type] = (acc[item.service_type] || 0) + 1;
        return acc;
      }, {});
      const detectedServiceType = Object.keys(serviceTypeCounts).reduce((a, b) => 
        serviceTypeCounts[a] > serviceTypeCounts[b] ? a : b
      , 'steam_iron');

      // Create order payload with items from cart
      // Note: Membership is automatically applied by backend for Steam Iron Daily Wear items
      const orderData = {
        service_type: detectedServiceType,
        pickup_address: formData.pickupAddress,
        delivery_address: formData.deliveryAddress || formData.pickupAddress,
        pickup_date: formData.pickupDate,
        pickup_time_slot: formData.pickupTimeSlot,
        delivery_date: formData.deliveryDate,
        delivery_time_slot: formData.deliveryTimeSlot,
        notes: formData.notes,
        payment_method: "cash",
        payment_status: "pending",
        items: cart.map((item) => ({
          item_id: item.id,
          item_type: item.service_type,
          item_name: item.item_name,
          quantity: item.quantity,
          price_per_item: item.price,
          original_price: item.price,
        })),
      };

      const response = await api.createOrder(orderData);

      if (response.success) {
        setSuccess(true);
        setOrderNumber(response.data.order_number || "N/A");

        // Clear cart from session storage ONLY after successful order
        sessionStorage.removeItem("orderCart");
        sessionStorage.removeItem("orderMembership");

        // Reset form
        setFormData({
          pickupAddress: "",
          deliveryAddress: "",
          pickupDate: "",
          pickupTimeSlot: "",
          deliveryDate: "",
          deliveryTimeSlot: "",
          notes: "",
          phone: "",
        });

        // Redirect after 3 seconds
        setTimeout(() => {
          navigate("/profile");
        }, 3000);
      } else {
        setError(response.message || "Failed to place order");
        setSubmitting(false); // Re-enable submission on error
      }
    } catch (err) {
      console.error("Error placing order:", err);
      setError(err.message || "An error occurred while placing your order");
      setSubmitting(false); // Re-enable submission on error
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page (SelectItems) without clearing cart
  };

  // Redirect admin users
  if (userRole === "admin" || userRole === "manager") {
    navigate("/admin/dashboard");
    return null;
  }

  // Require login
  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <Container>
      <Content>
        <Title>Place New Order</Title>
        <Subtitle>Schedule your laundry pickup and delivery</Subtitle>

        {success && (
          <SuccessMessage
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>Order Placed Successfully!</h3>
            <p>
              Your order number is: <strong>{orderNumber}</strong>
            </p>
            <p>We'll contact you shortly to confirm pickup details.</p>
            <p>Redirecting to your profile...</p>
          </SuccessMessage>
        )}

        {error && (
          <ErrorMessage
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </ErrorMessage>
        )}

        {cart.length > 0 && (
          <Card
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '20px' }}
          >
            <InfoBox>
              <InfoTitle>Order Summary</InfoTitle>
              {cart.map(item => (
                <InfoText key={item.id}>
                  {item.item_name} × {item.quantity} - ₹{(item.price * item.quantity).toFixed(2)}
                </InfoText>
              ))}
              <InfoText style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                <strong>Total Items:</strong> {cart.reduce((sum, item) => sum + item.quantity, 0)}
                {cart.reduce((sum, item) => sum + item.quantity, 0) < 10 && (
                  <span style={{ color: '#f44336', marginLeft: '10px' }}>
                    (Minimum 10 items required)
                  </span>
                )}
              </InfoText>
              {(() => {
                const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
                let regularTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                let membershipDiscount = 0;
                let membershipItemsCount = 0;
                
                if (membership && membership.clothes_remaining > 0) {
                  // Calculate how many items can be covered by membership
                  let remainingCoverage = membership.clothes_remaining;
                  let totalCoveredAmount = 0;
                  
                  cart.forEach(item => {
                    // Check if item is Steam Iron service type
                    const isServiceTypeSteamIron = item.service_type && item.service_type.toLowerCase() === 'steam_iron';
                    
                    // Check if item is Daily Wear subcategory (case-insensitive, handles null/undefined)
                    const subcategoryLower = item.subcategory ? item.subcategory.toLowerCase().trim() : '';
                    const isSubcategoryDailyWear = subcategoryLower === 'daily wear' || subcategoryLower.includes('daily');
                    
                    // Only steam iron daily wear items are eligible for membership
                    if (isServiceTypeSteamIron && isSubcategoryDailyWear && remainingCoverage > 0) {
                      const itemsTocover = Math.min(item.quantity, remainingCoverage);
                      const itemPrice = item.price;
                      totalCoveredAmount += itemsTocover * itemPrice;
                      membershipItemsCount += itemsTocover;
                      remainingCoverage -= itemsTocover;
                    }
                  });
                  
                  membershipDiscount = totalCoveredAmount;
                }

                const finalTotal = Math.max(0, regularTotal - membershipDiscount);

                return (
                  <>
                    {membership && membership.clothes_remaining > 0 && membershipItemsCount > 0 && (
                      <InfoText style={{ color: '#4caf50', fontWeight: 600, marginTop: '8px', padding: '12px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #4caf50' }}>
                        👕 <strong>Membership Benefit:</strong> {membershipItemsCount} daily wear items will be FREE! You have {membership.clothes_remaining} clothes remaining.
                      </InfoText>
                    )}
                    <div style={{ marginTop: '12px', padding: '16px', background: '#f5f5f5', borderRadius: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#666', fontSize: '15px' }}>
                        <span>Subtotal:</span>
                        <span>₹{regularTotal.toFixed(2)}</span>
                      </div>
                      {membershipDiscount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#4caf50', fontSize: '15px', fontWeight: 600 }}>
                          <span>Membership Discount:</span>
                          <span>-₹{membershipDiscount.toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '20px', color: 'var(--primary-color)', paddingTop: '12px', borderTop: '2px solid var(--primary-color)' }}>
                        <span>Total to Pay:</span>
                        <span>₹{finalTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </InfoBox>
          </Card>
        )}

        <Card initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <InfoBox>
            <InfoTitle>Payment Information</InfoTitle>
            <InfoText>
              💵 Cash on Delivery available
            </InfoText>
            <InfoText style={{ marginTop: '5px' }}>
              ✓ Online payment will be available soon
            </InfoText>
          </InfoBox>

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Pickup Address *</Label>
              <TextArea
                name="pickupAddress"
                value={formData.pickupAddress}
                onChange={handleChange}
                placeholder="Enter your complete pickup address"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Delivery Address (Leave blank if same as pickup)</Label>
              <TextArea
                name="deliveryAddress"
                value={formData.deliveryAddress}
                onChange={handleChange}
                placeholder="Enter delivery address if different from pickup"
              />
            </FormGroup>

            <FormGroup>
              <Label>Pickup Date *</Label>
              <Input
                type="date"
                name="pickupDate"
                value={formData.pickupDate}
                onChange={handleChange}
                min={getMinPickupDate()}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Pickup Time Slot *</Label>
              <Select
                name="pickupTimeSlot"
                value={formData.pickupTimeSlot}
                onChange={handleChange}
                required
                disabled={!formData.pickupDate}
              >
                <option value="">Select time slot</option>
                {getAvailablePickupSlots().map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>
                Preferred Delivery Date (Min: 24 hours after pickup, Max: 3 days)
              </Label>
              <Input
                type="date"
                name="deliveryDate"
                value={formData.deliveryDate}
                onChange={handleChange}
                min={getMinDeliveryDate()}
                max={getMaxDeliveryDate()}
                disabled={!formData.pickupDate}
              />
            </FormGroup>

            <FormGroup>
              <Label>Delivery Time Slot</Label>
              <Select
                name="deliveryTimeSlot"
                value={formData.deliveryTimeSlot}
                onChange={handleChange}
                disabled={!formData.deliveryDate}
              >
                <option value="">Select time slot</option>
                {deliveryTimeSlots.map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Contact Phone (Optional)</Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your contact number"
              />
            </FormGroup>

            <FormGroup>
              <Label>Special Instructions (Optional)</Label>
              <TextArea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any special instructions for pickup or cleaning?"
              />
            </FormGroup>

            <ButtonGroup>
              <SubmitButton type="submit" disabled={submitting}>
                {submitting ? "Placing Order..." : "Place Order"}
              </SubmitButton>
              <CancelButton type="button" onClick={handleCancel} disabled={submitting}>
                Cancel
              </CancelButton>
            </ButtonGroup>
          </form>
        </Card>
      </Content>
    </Container>
  );
};

export default PlaceOrder;
